/*
 * Lemonade Training Oy copy rights all right reserved.
 * Athlete results and the one definition of an athlete's current value.
 *
 * Runtime helpers here execute inside Cloud Functions (firebase-admin) and on
 * both clients (firebase client SDK), so Timestamps are duck-typed and the
 * firebase import is type-only.
 */

import type { Timestamp } from "firebase/firestore";
import {
  MOVEMENT_MEASURE_TYPE,
  MovementRegistryEntry,
  SCORING_TYPE,
  ScoringType,
} from "./movementRegistry";

/**
 * What kind of measurement a stored result represents. Supersedes the legacy
 * `inputMode`, which stays on disk and on the wire for old clients.
 *
 * - `tested`     - a max the athlete performed (kg/reps/calories/distance)
 * - `workingSet` - a submaximal set; `value` is a Brzycki ESTIMATE
 * - `timeTrial`  - a timed effort (`measureType: "time"`)
 * - `unlock`     - a binary gymnastics capability (`measureType: "binary"`)
 */
export const MOVEMENT_RECORD_KIND = {
  TESTED: "tested",
  WORKING_SET: "workingSet",
  TIME_TRIAL: "timeTrial",
  UNLOCK: "unlock",
} as const;

export type MovementRecordKind =
  (typeof MOVEMENT_RECORD_KIND)[keyof typeof MOVEMENT_RECORD_KIND];

export const MOVEMENT_RECORD_KINDS: readonly MovementRecordKind[] =
  Object.values(MOVEMENT_RECORD_KIND);

export function isMovementRecordKind(obj: unknown): obj is MovementRecordKind {
  return (
    typeof obj === "string" &&
    (MOVEMENT_RECORD_KINDS as readonly string[]).includes(obj)
  );
}

/**
 * Whether a kind can be the athlete's current value or displayed PR. A working
 * set is an estimate the athlete never lifted, so it is never either. Every
 * new kind must decide here.
 */
const IS_CURRENT_ELIGIBLE: Record<MovementRecordKind, boolean> = {
  tested: true,
  workingSet: false,
  timeTrial: true,
  unlock: true,
};

export const CURRENT_ELIGIBLE_KINDS: readonly MovementRecordKind[] =
  MOVEMENT_RECORD_KINDS.filter((kind) => IS_CURRENT_ELIGIBLE[kind]);

/** Legacy on-disk discriminator, kept for old clients. */
export type LegacyInputMode = "oneRepMax" | "workingSet" | "workoutTime";

/** Any date representation found on stored results, legacy ones included. */
export type StoredDate = Timestamp | Date | string | number;

/**
 * One stored result: `Users/{uid}/Records_1RM/{recordId}`.
 *
 * Typed as actually stored: the writer emits `null` for absent optionals, and
 * documents written before server-side logging lack `movementRegistryID`,
 * `date`, `inputMode` and `kind` altogether. The owner can still write these
 * documents directly, so readers treat every field as untrusted and never
 * base an authorisation decision on one.
 */
export type AthleteResult = {
  /** Firestore document id; set by the reader, not stored in the document. */
  id: string;
  movementRegistryID?: string | null;
  /** Display name at write time. Never used for matching. */
  name?: string | null;
  /** The scored number. For `workingSet` this is a Brzycki estimate. */
  value: number;
  e1RM?: number | null;
  weight?: number | null;
  reps?: number | null;
  sets?: number | null;
  time?: number | null;
  calories?: number | null;
  distance?: number | null;
  unlocked?: boolean | null;
  /** @deprecated Read `kind`, or derive it with {@link resolveRecordKind}. */
  inputMode?: LegacyInputMode | null;
  /** Absent on records written before it existed: derive, never trust presence. */
  kind?: MovementRecordKind | null;
  /**
   * Athlete-authored text, at most `MOVEMENT_RESULT_NOTES_MAX_LENGTH` after
   * trim. Personal data: never logged, never in an error, analytics event or
   * LLM prompt.
   */
  notes?: string | null;
  /** When the effort was performed. The sort key. */
  date?: StoredDate | null;
  createdAt?: StoredDate | null;
  updatedAt?: StoredDate | null;
  /**
   * Soft delete; truthy hides the record from every derived number. Never
   * query `where("deletedAt", "==", null)`: documents lacking the field do not
   * match it. Filter in memory with {@link filterActiveRecords}. Purged 30
   * days after deletion, and immediately on account erasure.
   */
  deletedAt?: Timestamp | Date | null;
  /** uid of the actor who soft-deleted. Server-written. */
  deletedBy?: string | null;
  restoredAt?: Timestamp | Date | null;
  restoredBy?: string | null;
  /** uid of the actor who last edited. Server-written. */
  updatedBy?: string | null;
};

/** A pointer to one record, with enough to display it without a re-read. */
export type MovementStateEntry = {
  value: number;
  kind: MovementRecordKind;
  recordId: string;
  /** The record's resolved date; `null` only for a record carrying no date at all. */
  date: Timestamp | Date | null;
  /** Present only when the record carries a boolean `unlocked`. */
  unlocked?: boolean;
};

/**
 * Why a value is the athlete's current one. Adding a member breaks any
 * exhaustive `switch` in a consumer: change it here first.
 */
export const MOVEMENT_CURRENT_BASIS = {
  LATEST_TESTED: "latestTested",
} as const;

export type MovementCurrentBasis =
  (typeof MOVEMENT_CURRENT_BASIS)[keyof typeof MOVEMENT_CURRENT_BASIS];

export type MovementCurrentValue = MovementStateEntry & {
  basis: MovementCurrentBasis;
};

/**
 * The single derived answer to "what is this athlete's value for movement X".
 * Written only by Cloud Functions.
 *
 * `current` is the MOST RECENT non-working-set record by date, for EVERY
 * measure type, binary included. There are no sticky unlocks: a "Not yet"
 * logged after an unlock removes the unlock from `current`.
 */
export type MovementState = {
  movementRegistryID: string;
  /**
   * `null` is the empty state ("no tested result yet"). Test `current != null`
   * to ask whether the athlete has a value, never the entry's existence.
   */
  current: MovementCurrentValue | null;
  /** The fact behind `current`; `current` is the policy output. */
  latestTested: MovementStateEntry | null;
  /** Best non-working-set value ever. Displayed as "PR". */
  best: MovementStateEntry | null;
  /**
   * Best value ever INCLUDING working-set estimates. NEVER DISPLAY IT: it can
   * be a number the athlete never lifted. Its only consumer is `isNewPR`, so
   * PR detection and XP stay exactly as they were.
   */
  bestAttempt: MovementStateEntry | null;
  /** Most recent record of any kind, working sets included. */
  latest: MovementStateEntry | null;
  /** Active records, all kinds. */
  attemptCount: number;
  /** Active records whose kind is current-eligible. */
  testedCount: number;
  /** Resolved date of the most recent active record. */
  lastLoggedAt: Timestamp | Date | null;
  /** Records received, soft-deleted included. */
  sourceCount: number;
  /**
   * Built from a capped per-movement read, so `best`, `bestAttempt`, `latest`
   * and the counts are unreliable. `current` and `latestTested` are carried over
   * from the stored entry (cleared when the edited or deleted record is the one
   * they point at; the projection stands when nothing is stored). The radar and
   * the cascade skip the entry. Must alert, never degrade quietly.
   */
  truncated: boolean;
};

/** Document id of the map document at `Users/{uid}/AthleteProfile/{id}`. */
export const MOVEMENT_STATE_DOC_ID = "movementState";

/** Bump when the stored shape changes, so the reconciler re-derives. */
export const MOVEMENT_STATE_SCHEMA_VERSION = 1;

export type MovementStateComputedBy =
  "callable" | "trigger" | "reconciler" | "backfill";

/** `Users/{uid}/AthleteProfile/movementState`. */
export type MovementStateDoc = {
  schemaVersion: number;
  /**
   * Keyed by registry-validated `movementRegistryID`; a movement never logged
   * has no entry. Group with a `Map`, not a plain object: ids like
   * "constructor" are valid.
   */
  movements: Partial<Record<string, MovementState>>;
  computedAt: Timestamp | Date;
  computedBy: MovementStateComputedBy;
};

export function isMovementState(obj: unknown): obj is MovementState {
  if (typeof obj !== "object" || obj === null) return false;
  const s = obj as MovementState;
  return (
    typeof s.movementRegistryID === "string" &&
    typeof s.attemptCount === "number" &&
    (s.current === null ||
      (typeof s.current === "object" && typeof s.current.value === "number"))
  );
}

/**
 * Accepts `Date`, ISO strings, epoch milliseconds, and Timestamps from either
 * firebase SDK (duck-typed: they are different classes, so `instanceof` would
 * fail in one environment). Anything unparseable is `null`.
 */
export function coerceStoredDate(v: unknown): Date | null {
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  if (typeof v === "number") {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isFinite(t) ? new Date(t) : null;
  }
  if (typeof v === "object" && v !== null) {
    const ts = v as { toDate?: unknown; seconds?: unknown; _seconds?: unknown };
    if (typeof ts.toDate === "function") {
      return coerceStoredDate((ts.toDate as () => unknown).call(v));
    }
    // `_seconds` is how a Timestamp arrives after callable JSON serialisation.
    const seconds = typeof ts.seconds === "number" ? ts.seconds : ts._seconds;
    if (typeof seconds === "number") {
      return coerceStoredDate(seconds * 1000);
    }
  }
  return null;
}

/** `date`, else `createdAt`, else `updatedAt`; `null` when none parses. */
export function resolveRecordDate(
  record: Pick<AthleteResult, "date" | "createdAt" | "updatedAt">,
): Date | null {
  return (
    coerceStoredDate(record.date) ||
    coerceStoredDate(record.createdAt) ||
    coerceStoredDate(record.updatedAt)
  );
}

/**
 * A valid stored `kind` wins; otherwise derive from the movement and the
 * legacy `inputMode`. Binary is checked before `inputMode` because binary
 * results are logged with `inputMode: "oneRepMax"`. An absent `inputMode`
 * means `tested`: every such record predates working-set logging.
 */
export function resolveRecordKind(
  record: Pick<AthleteResult, "kind" | "inputMode">,
  movement: Pick<MovementRegistryEntry, "measureType">,
): MovementRecordKind {
  if (isMovementRecordKind(record.kind)) return record.kind;
  if (movement.measureType === MOVEMENT_MEASURE_TYPE.BINARY) {
    return MOVEMENT_RECORD_KIND.UNLOCK;
  }
  if (record.inputMode === "workingSet") {
    return MOVEMENT_RECORD_KIND.WORKING_SET;
  }
  if (
    record.inputMode === "workoutTime" ||
    movement.measureType === MOVEMENT_MEASURE_TYPE.TIME
  ) {
    return MOVEMENT_RECORD_KIND.TIME_TRIAL;
  }
  return MOVEMENT_RECORD_KIND.TESTED;
}

/** Drops soft-deleted records. Legacy documents lacking `deletedAt` are active. */
export function filterActiveRecords<T extends Pick<AthleteResult, "deletedAt">>(
  records: readonly T[],
): T[] {
  return records.filter((r) => !r.deletedAt);
}

/** Stored ids are owner-writable and unbounded; keep error messages short. */
function clip(s: string): string {
  return s.length > 64 ? `${s.slice(0, 64)}…` : s;
}

type Resolved = {
  record: AthleteResult;
  kind: MovementRecordKind;
  when: Date | null;
  createdMs: number;
};

function toEntry(r: Resolved): MovementStateEntry {
  const entry: MovementStateEntry = {
    value: r.record.value,
    kind: r.kind,
    recordId: r.record.id,
    date: r.when,
  };
  if (typeof r.record.unlocked === "boolean") {
    entry.unlocked = r.record.unlocked;
  }
  return entry;
}

function newestFirst(a: Resolved, b: Resolved): number {
  const aWhen = a.when ? a.when.getTime() : -Infinity;
  const bWhen = b.when ? b.when.getTime() : -Infinity;
  if (aWhen !== bWhen) return bWhen - aWhen;
  if (a.createdMs !== b.createdMs) return b.createdMs - a.createdMs;
  // Plain code-unit order: locale-aware comparison varies between runtimes.
  if (a.record.id === b.record.id) return 0;
  return a.record.id < b.record.id ? 1 : -1;
}

/** Ties keep the earlier element, i.e. the more recent record. */
function pickBest(
  sorted: readonly Resolved[],
  scoringType: ScoringType,
): Resolved | null {
  let best: Resolved | null = null;
  for (const r of sorted) {
    if (best === null) {
      best = r;
    } else if (scoringType === SCORING_TYPE.LOWER_IS_BETTER) {
      if (r.record.value < best.record.value) best = r;
    } else if (r.record.value > best.record.value) {
      best = r;
    }
  }
  return best;
}

/**
 * THE resolver. Pass every record for one (athlete, movement), soft-deleted
 * included, in any order: do not pre-filter or pre-sort, and never read them
 * with `orderBy("date")`, which silently drops documents lacking `date`.
 *
 * Records with no date at all rank oldest; they can still be `best`, and are
 * `current` only when no dated eligible record exists. Records with a
 * non-finite `value` count in `sourceCount` only. A record linked to a
 * different movement is a caller bug and throws.
 *
 * `bestAttempt` counts every record passed. For PR and XP parity with the
 * server's rule, pass exactly `where("movementRegistryID", "==", id)`, with
 * no id or name fallback.
 */
export function computeMovementState(
  records: readonly AthleteResult[],
  movement: Pick<MovementRegistryEntry, "id" | "scoringType" | "measureType">,
  opts: { truncated?: boolean } = {},
): MovementState {
  const resolved: Resolved[] = [];
  for (const record of filterActiveRecords(records)) {
    if (
      typeof record.movementRegistryID === "string" &&
      record.movementRegistryID !== "" &&
      record.movementRegistryID !== movement.id
    ) {
      throw new Error(
        `computeMovementState: record ${clip(record.id)} belongs to ${clip(record.movementRegistryID)}, not ${movement.id}`,
      );
    }
    if (!Number.isFinite(record.value)) continue;
    const created = coerceStoredDate(record.createdAt);
    resolved.push({
      record,
      kind: resolveRecordKind(record, movement),
      when: resolveRecordDate(record),
      createdMs: created ? created.getTime() : 0,
    });
  }

  const sorted = resolved.sort(newestFirst);
  const eligible = sorted.filter((r) => IS_CURRENT_ELIGIBLE[r.kind]);
  const latestTested = eligible.length > 0 ? toEntry(eligible[0]) : null;
  const best = pickBest(eligible, movement.scoringType);
  const bestAttempt = pickBest(sorted, movement.scoringType);

  return {
    movementRegistryID: movement.id,
    current: latestTested
      ? { ...latestTested, basis: MOVEMENT_CURRENT_BASIS.LATEST_TESTED }
      : null,
    latestTested,
    best: best ? toEntry(best) : null,
    bestAttempt: bestAttempt ? toEntry(bestAttempt) : null,
    latest: sorted.length > 0 ? toEntry(sorted[0]) : null,
    attemptCount: sorted.length,
    testedCount: eligible.length,
    lastLoggedAt: sorted.length > 0 ? sorted[0].when : null,
    sourceCount: records.length,
    truncated: opts.truncated === true,
  };
}

/**
 * The one entry a profile surface (radar, axis detail) uses for a movement.
 * With `movementStateRead` on it is `current`, the value the radar scores.
 * Off, binary is still latest-wins and other shapes keep the PR, else
 * the newest result. `bestAttempt` is never shown.
 */
export function profileEntry(
  state: Pick<MovementState, "current" | "best" | "latest">,
  scoringType: ScoringType,
  stateReadLive: boolean,
): MovementStateEntry | null {
  if (stateReadLive) return state.current;
  if (scoringType === SCORING_TYPE.BINARY) return state.current ?? state.latest;
  return state.best ?? state.latest;
}

/**
 * Whether `newValue` is a new PR. Reads `bestAttempt`, never `best`, so PR
 * detection and PR XP are identical to the server's pre-existing rule: the
 * best over every stored value regardless of kind.
 */
export function isNewPR(
  state: Pick<MovementState, "bestAttempt"> | null,
  newValue: number,
  scoringType: ScoringType,
): boolean {
  const existingBest =
    state && state.bestAttempt ? state.bestAttempt.value : null;
  if (scoringType === SCORING_TYPE.BINARY) {
    return existingBest === null || (existingBest === 0 && newValue > 0);
  }
  if (scoringType === SCORING_TYPE.LOWER_IS_BETTER) {
    return existingBest === null || newValue < existingBest;
  }
  return existingBest === null || newValue > existingBest;
}
